/**
 * When a function runs by itself — UX-02.
 *
 * `POST /v1/projects/{id}/functions/{name}/schedule` has existed since
 * functions shipped, and the function listing has carried each schedule
 * with it the whole time. The dashboard's own EdgeFunction type simply did
 * not mention the field, so the answer arrived on every page load and was
 * thrown away: a function could be put on a schedule by an agent or the
 * CLI and there was nowhere to see that it was on one.
 *
 * WHY THIS IMPORTS THE PARSER RATHER THAN CHECKING THE STRING
 *
 * The control plane refuses a schedule it cannot parse, using
 * @nimblybase/cron. A second opinion written here would be a second
 * definition of what a valid schedule is, and the two would disagree
 * eventually - the dashboard rejecting something the platform accepts, or
 * worse, accepting something it does not and turning a typo into a 400 from
 * a control the page drew itself.
 */

import { describeCron, parseCron } from '@nimblybase/cron'

/** Every schedule is UTC. Saying so once, where it is set. */
export const UTC_NOTE =
  'Schedules run in UTC, not your timezone or your users’. The platform does not move them '
  + 'for daylight saving, so a job set for 09:00 stays at 09:00 UTC all year.'

export type ScheduleCheck =
  | { ok: true; describes: string }
  | { ok: false; why: string }

/**
 * What the platform will make of this expression, before sending it.
 *
 * The same parser the control plane validates with, so a schedule this
 * accepts is one the API accepts. The description is the platform's own
 * words for it - if the two ever disagreed, the person would be told one
 * thing here and another after saving.
 */
export function check(expression: string): ScheduleCheck {
  const parsed = parseCron(expression)
  if ('error' in parsed) return { ok: false, why: parsed.error }
  return { ok: true, describes: describeCron(parsed) }
}

/**
 * How a function's current schedule reads in a list.
 *
 * `null` and `undefined` both mean "not scheduled", and they arrive from
 * different places: a function that never had one, and an older response
 * that did not carry the field. Neither is worth distinguishing on screen.
 */
export function scheduleLabel(schedule: string | null | undefined): string {
  if (schedule == null || schedule === '') return 'Runs when called'
  const parsed = parseCron(schedule)
  // A schedule the platform stored but this build cannot read is shown as
  // written rather than hidden. Hiding it would say "runs when called",
  // which is the one answer that is wrong.
  return 'error' in parsed ? `Scheduled: ${schedule}` : `Runs ${describeCron(parsed)}`
}
